/* eslint-disable spire/export-styles */
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button from "@insite/mobius/Button";
import { HasHistory, History, withHistory } from "@insite/mobius/utilities/HistoryContext";
import * as cssLinter from "css";
import * as React from "react";
import { connect } from "react-redux";
import styled, { css } from "styled-components";

const enum fields {
    variant = "variant",
    label = "label",
    link = "link",
    externalUrl = "externalUrl",
    mailTo = "mailTo",
    tel = "tel",
    file = "file",
    alignment = "alignment",
    customCSS = "customCSS",
    textColor = "textColor",
    buttonColor = "buttonColor",
    marginLeft = "marginLeft",
    marginRight = "marginRight",
    btnMinWidth = "btnMinWidth",
    borderWidth = "buttonType",
    padding = "padding",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.variant]: "primary" | "secondary" | "tertiary";
        [fields.label]: string;
        [fields.link]: LinkFieldValue;
        [fields.alignment]: string;
        [fields.customCSS]: string;
        [fields.textColor]: string;
        [fields.buttonColor]: string;
        [fields.marginLeft]: string;
        [fields.marginRight]: string;
        [fields.btnMinWidth]: string;
        [fields.externalUrl]: string;
        [fields.mailTo]: string;
        [fields.tel]: string;
        [fields.file]: string;
        [fields.padding]: string;
    };
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => getLink(state, ownProps.fields.link);

const onClick = (
    history: History,
    link: string | undefined,
    mailTo: string | undefined,
    tel: string | undefined,
    externalUrl: string | undefined,
    file: string | undefined,
) => {
    if (link) {
        history.push(link);
    }

    if (mailTo) {
        window.location.href = `mailto:${mailTo}`;
    }

    if (tel) {
        window.location.href = `tel:${tel}`;
    }

    if (externalUrl) {
        window.location.href = externalUrl;
    }

    if (file) {
        window.open(file, "_blank");
    }
};

type Props = HasHistory & OwnProps & ReturnType<typeof mapStateToProps>;

const CmsButton: React.FunctionComponent<Props> = ({ fields, history, url }: Props) => {
    const wrapperStyles = {
        css: css`
            width: 100%;
            display: flex;
            justify-content: ${fields.alignment ?? "left"};
        `,
    };

    const buttonStyles = {
        css: css`
            color: ${fields.textColor};
            border: 2px solid ${fields.buttonColor};
            z-index: 2;
            margin-left: ${fields.marginLeft}%;
            maring-right: ${fields.marginRight}%;
            min-width: ${fields.btnMinWidth}%;
            padding: ${fields.padding}px;
        `,
    };

    const customCssWrapper = {
        css: css`
            ${fields.customCSS}
        `,
    };

    return (
    <StyledWrapper {...customCssWrapper}>
        <StyledWrapper {...wrapperStyles}>
            <Button
                color={fields.buttonColor}
                {...buttonStyles}
                variant={fields.variant}
                onClick={() => onClick(history, url, fields.mailTo, fields.tel, fields.externalUrl, fields.file)}
            >
                {fields.label}
            </Button>
        </StyledWrapper>
    </StyledWrapper>
    );
};

const defaultCustomCss = `.wrapper {
}

.button {
}
`;

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withHistory(CmsButton)),
    definition: {
        group: "Basic",
        icon: "rectangle-wide",
        fieldDefinitions: [
            {
                fieldType: "General",
                name: fields.buttonColor,
                editorTemplate: "ColorPickerField",
                displayName: "Button Color",
                defaultValue: "#f16906",
            },
            {
                fieldType: "General",
                name: fields.textColor,
                editorTemplate: "ColorPickerField",
                displayName: "Text Color",
                defaultValue: "white",
            },
            {
                name: fields.variant,
                displayName: "Button Variant",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Primary", value: "primary" },
                    { displayName: "Secondary", value: "secondary" },
                    { displayName: "Tertiary", value: "tertiary" },
                ],
                defaultValue: "primary",
                fieldType: "General",
                tab: basicTab,
            },
            {
                name: fields.label,
                displayName: "Button Label",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                tab: basicTab,
            },
            {
                name: fields.link,
                displayName: "Button Link",
                editorTemplate: "LinkField",
                defaultValue: {
                    value: "",
                    type: "Page",
                },
                fieldType: "General",
                tab: basicTab,
            },
            {
                name: fields.externalUrl,
                displayName: "External Url",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
            {
                name: fields.mailTo,
                displayName: "Mailto",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
            {
                name: fields.tel,
                displayName: "Tel",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
            {
                name: fields.file,
                displayName: "File path",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
            {
                name: fields.marginLeft,
                displayName: "Button Margin Left (%)",
                editorTemplate: "TextField",
                defaultValue: "0",
                fieldType: "Translatable",
            },
            {
                name: fields.marginRight,
                displayName: "Button Margin Right (%)",
                editorTemplate: "TextField",
                defaultValue: "0",
                fieldType: "Translatable",
            },
            {
                name: fields.btnMinWidth,
                displayName: "Button Min Width (%)",
                editorTemplate: "TextField",
                defaultValue: "100",
                fieldType: "Translatable",
            },
            {
                name: fields.padding,
                displayName: "Button Padding (px)",
                editorTemplate: "TextField",
                defaultValue: "0",
                fieldType: "Translatable",
            },
            {
                name: fields.alignment,
                displayName: "Button Alignment",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Left", value: "flex-start" },
                    { displayName: "Center", value: "center" },
                    { displayName: "Right", value: "flex-end" },
                ],
                defaultValue: "primary",
                fieldType: "General",
                tab: basicTab,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
